// Bunker Hill org seed — seats from the FMEA Responsible column and Paul's
// review routing (construct.R13 · § 9.2).

import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { Crew, CrewId, Person, Seat, SeatAssignment, SeatId } from '@/versions/v1/schema/seat';
import { diffMinutes, previousShiftWindow, shiftOf } from '@/versions/v1/shared/clock.derive';

export const BUNKER_HILL_PERSONS: readonly Person[] = [
  { id: 'person-spark', name: 'S. Park' },
  { id: 'person-dokafor', name: 'D. Okafor' },
  { id: 'person-gm', name: 'Plant Manager' },
  { id: 'person-senior-met', name: 'L. Chen' }, // [syn]
  { id: 'person-bfletcher', name: 'B. Fletcher' }, // [syn] A7 permit crew; existing maintenance name
  { id: 'person-jsantos', name: 'J. Santos' }, // [syn] A7 permit crew
  { id: 'person-tburns', name: 'T. Burns' }, // [syn] A7 permit crew; existing maintenance name
  { id: 'person-amarks', name: 'A. Marks' }, // [syn] A7 permit crew
  { id: 'person-mgoh', name: 'M. Goh' }, // [syn] A7 permit crew
  { id: 'person-lpark', name: 'L. Park' }, // [syn] A7 permit crew
  { id: 'person-kjones', name: 'K. Jones' }, // [syn] A7 permit crew; existing maintenance name
  { id: 'person-rpatel', name: 'R. Patel' }, // [syn] A7 permit crew
  { id: 'person-tnguyen', name: 'T. Nguyen' }, // [syn] A7 permit crew
  { id: 'person-jsmith', name: 'J. Smith' }, // [syn] A7 BM01 isolation permit; WO-441 this shift
  // [syn] A8 training roster additions — kept contiguous for the shared seed.
  { id: 'person-dlee', name: 'D. Lee' }, // [syn]
  { id: 'person-kbrennan', name: 'K. Brennan' }, // [syn]
  { id: 'person-mnavarro', name: 'M. Navarro' }, // [syn]
  { id: 'person-dpham', name: 'D. Pham' }, // [syn]
  { id: 'person-tobi', name: 'T. Obi' }, // [syn]
];

export const BUNKER_HILL_SEATS: readonly Seat[] = [
  { id: 'seat-met', roleId: 'metallurgist', label: 'Metallurgist (Ext 2220)' },
  { id: 'seat-senior-met', roleId: 'senior-metallurgist', label: 'Senior metallurgist' },
  { id: 'seat-sre', roleId: 'maintenance', label: 'Senior reliability engineer' },
  {
    id: 'seat-plant-mgr',
    roleId: 'gm',
    label: 'Plant Manager (Ext 2100)',
    accountable: true,
  },
  { id: 'seat-supt', roleId: 'superintendent', label: 'Superintendent' },
  { id: 'seat-ops-supervisor', roleId: 'ops-supervisor', label: 'Ops supervisor' },
  { id: 'seat-cro', roleId: 'cro', label: 'Control room operator' },
  { id: 'seat-operator', roleId: 'operator', label: 'Field operator' },
  { id: 'seat-maintenance', roleId: 'maintenance', label: 'Maintenance' },
];

export const BUNKER_HILL_CREWS: readonly Crew[] = [
  { id: 'crew-a', label: 'A Crew', pattern: 'day' },
  { id: 'crew-b', label: 'B Crew', pattern: 'night' },
  { id: 'crew-c', label: 'C Crew', pattern: 'day' },
  { id: 'crew-d', label: 'D Crew', pattern: 'night' },
];

/** [syn] Persona crew assignment for roster-period reads (#285). */
export const PERSONA_CREW: Readonly<Partial<Record<PlantRoleId, CrewId>>> = {
  cro: 'crew-c',
};

/** [syn] Four crews on a 2-2 rotation from 2026-05-24 — A/C day, B/D night. */
export type RosterShift = {
  date: string;
  dayCrewId: CrewId;
  nightCrewId: CrewId;
  dayHeadcount: number;
  nightHeadcount: number;
  daySeniors: number;
  nightSeniors: number;
};

const ROSTER_START = '2026-05-24';
const DESIGN_HEADCOUNT = 6;
const DESIGN_SENIORS = 2;

function rosterBlockIndex(date: string): number {
  const start = `${ROSTER_START}T06:00`;
  const at = `${date}T06:00`;
  const days = Math.floor(diffMinutes(at, start) / (24 * 60));
  return Math.floor(days / 2) % 2;
}

function rosterForDate(date: string): RosterShift {
  const block = rosterBlockIndex(date);
  const dayCrewId: CrewId = block === 0 ? 'crew-a' : 'crew-c';
  const nightCrewId: CrewId = block === 0 ? 'crew-b' : 'crew-d';
  const isShortStaffed = date === '2026-06-07' && nightCrewId === 'crew-d';
  return {
    date,
    dayCrewId,
    nightCrewId,
    dayHeadcount: DESIGN_HEADCOUNT,
    nightHeadcount: isShortStaffed ? 4 : DESIGN_HEADCOUNT,
    daySeniors: DESIGN_SENIORS,
    nightSeniors: isShortStaffed ? 1 : DESIGN_SENIORS,
  };
}

/** [syn] Roster lookup — keyed to the shift window start, not the calendar date of the instant. */
export function rosterShiftAt(at: string): RosterShift {
  const { start } = shiftOf(at);
  const date = start.split('T')[0]!;
  return rosterForDate(date);
}

export const BUNKER_HILL_ASSIGNMENTS: readonly SeatAssignment[] = [
  { seatId: 'seat-met', personId: 'person-spark', from: '2026-01-01' },
  { seatId: 'seat-senior-met', personId: 'person-senior-met', from: '2026-01-01' },
  { seatId: 'seat-cro', personId: 'person-dokafor', from: '2026-01-01' },
  { seatId: 'seat-ops-supervisor', personId: 'person-dlee', from: '2026-01-01' },
  { seatId: 'seat-plant-mgr', personId: 'person-gm', from: '2026-01-01' },
  // [syn] A8 training assignments — appended contiguously for the shared seed.
  { seatId: 'seat-supt', personId: 'person-kbrennan', from: '2026-01-01' },
  { seatId: 'seat-ops-supervisor', personId: 'person-mnavarro', from: '2026-01-01' },
  { seatId: 'seat-cro', personId: 'person-dpham', from: '2026-01-01' },
  { seatId: 'seat-cro', personId: 'person-tobi', from: '2026-01-01' },
];

/** Primary seat per role — explicit keys when multiple seats share a role. */
export const PRIMARY_SEAT_BY_ROLE: Readonly<Partial<Record<PlantRoleId, SeatId>>> = {
  metallurgist: 'seat-met',
  'senior-metallurgist': 'seat-senior-met',
  maintenance: 'seat-maintenance',
  gm: 'seat-plant-mgr',
  superintendent: 'seat-supt',
  cro: 'seat-cro',
  operator: 'seat-operator',
  'ops-supervisor': 'seat-ops-supervisor',
};

/** Reconcile header chip short labels — keyed to PRIMARY_SEAT_BY_ROLE. */
export const ROLE_SHORT_LABEL: Readonly<Partial<Record<PlantRoleId, string>>> = {
  cro: 'CRO',
  operator: 'Operator',
  metallurgist: 'Met',
  'senior-metallurgist': 'Senior Met',
  gm: 'GM',
  superintendent: 'Superintendent',
  'ops-supervisor': 'Ops supervisor',
  maintenance: 'Maintenance',
};

export const APPROVAL_GATE_SEAT_ID: SeatId = 'seat-plant-mgr';

export function approvalGateSeatLabel(): string {
  return BUNKER_HILL_SEATS.find((row) => row.id === APPROVAL_GATE_SEAT_ID)?.label ?? 'Plant Manager';
}

export function crewLabelById(crewId: CrewId | string): string {
  return BUNKER_HILL_CREWS.find((row) => row.id === crewId)?.label ?? crewId;
}

/** Crew on shift at the session cursor — reads the [syn] roster via shiftOf. */
export function activeCrewId(at = '2026-06-08T11:30'): CrewId {
  const { kind } = shiftOf(at);
  const roster = rosterShiftAt(at);
  return kind === 'day' ? roster.dayCrewId : roster.nightCrewId;
}

export function activeCrewLabel(at = '2026-06-08T11:30'): string {
  return crewLabelById(activeCrewId(at));
}

export function activeCrewForShift(
  at: string,
  kind: 'day' | 'night',
): { crewId: CrewId; headcount: number; seniors: number } {
  const roster = rosterShiftAt(at);
  if (kind === 'day') {
    return {
      crewId: roster.dayCrewId,
      headcount: roster.dayHeadcount,
      seniors: roster.daySeniors,
    };
  }
  return {
    crewId: roster.nightCrewId,
    headcount: roster.nightHeadcount,
    seniors: roster.nightSeniors,
  };
}

/** The crew that handed over into the shift at `at` — the crew on the shift
 *  before the cursor's own: a day cursor reads the previous night's crew; a
 *  night cursor reads the same day's day crew. One helper, so the Daily
 *  meeting's handover and the Ops supervisor landing's carried card name the
 *  same crew at every cursor (#777; P13 r1 fixed the landing's copy). */
export function previousCrewForShift(at: string): { crewId: CrewId; headcount: number; seniors: number } {
  const previous = previousShiftWindow(at);
  return activeCrewForShift(previous.start, previous.kind);
}
